import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputNumber } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { Subject, distinctUntilChanged, filter, map, takeUntil } from 'rxjs';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { MappingStatusService } from '../../../services/mbrs/mapping-status.service';
import { ColumnSumValidationService, ColumnSumValidationConfig } from '../../../services/mbrs/column-sum-validation.service';
import { CrossReferenceService } from '../../../services/mbrs/cross-reference.service';
import { EnvironmentService } from '../../../services/common/environment.service';
import { FORM_AUTOSAVE_DEBOUNCE_MS } from '../../../utils/debounce-times.util';
import { buildEditModeLivePipeline } from '../../../utils/form-edit-pipeline.util';
import { pickSubset } from '../../../utils/pick-subset.util';
import { adoptIntoInlineEditSheet } from '../../../utils/adopt-remote-sheet-state.util';
import { SubmitForValidationComponent } from "../../common/submit-for-validation/submit-for-validation.component";
import { RichTextFieldComponent } from '../../common/rich-text-field/rich-text-field.component';
import { NegativeHighlightDirective } from '../../../directives/negative-highlight.directive';
import { SchemaNumericDirective } from '../../../directives/schema-numeric.directive';
import { NumericFieldPolicy, resolveNumericPolicy } from '../../../utils/numeric-policy.util';
import { SkeletonModule } from 'primeng/skeleton';
import {
  pickRole24SchemaFile,
  ROLE24_MEMBER_SUFFIXES,
} from '../../../services/mbrs/validators/role24-notes-related-party-transactions.rules';
import {
  activeDimensions,
  isCellVisible,
  resolvePrerequisiteValue,
} from '../../../services/mbrs/validators/dimension-visibility.util';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  TOAST_DURATION_DEFAULT_MS,
} from '../../../utils/toast-durations.util';
import { PlaceholderPipe } from '../../../utils/pipes/placeholder.pipe';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';
import { FieldLinkLabelComponent } from '../../common/field-link-label/field-link-label.component';
import { isControlMandatory } from '../../../utils/required-field.util';

import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import { FieldLinkTargetDirective, FieldLinkSectionSink } from '../../../directives/field-link-target.directive';
import { computedControlPredicate, revealValidationErrors, enableAndRevealErrors } from '../../../utils/validation-reveal.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import { finalizeSheetInit } from '../../../utils/sheet-init-finalize.util';
import {
  classifySheetInitError,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';
interface SchemaControl {
  qname: string;
  label: string;
  type: string;
  ui: {
    control: string;
    level?: number;
    note?: string;
    rows?: number;
    options?: string[];
    conditionalOn?: string;
    conditionalValue?: string;
    placeholder?: string;
    tableColumn?: boolean;
    readonly?: boolean;
    calculated?: boolean;
    linkTo?: {                  // NEW
      role: string;             // Role code (e.g., "210100")
      qname: string;            // Target qname in source role
    };
    inheritFromRole9?: boolean; // LEGACY (keep for backward compatibility)
    inheritFromRole?: string;   // LEGACY
  };
  required?: boolean;
  requiredIf?: {                // Cross-sheet conditional required
    sheet: string;              // Sheet key (e.g., 'filingPrerequisites')
    qname: string;              // Field qname in the other sheet
    equals: string;             // Value that makes this field required
  };
  validators?: Array<{ name: string; value?: any }>;
  error?: { [key: string]: string };  // Custom error messages
  customValidations?: Array<{   // NEW (Optional)
    type: string;
    message: string;
    rule?: any;
    condition?: any;
  }>;
}

/** Short-name → xbrl-member-suffix map. Total has no suffix (uses base qname). */
const MEMBER_SUFFIX_MAP: { [key: string]: string } = {
  'Parent': 'ParentMember',
  'JointControl': 'JointControlOrSignificantInfluenceMember',
  'Subsidiaries': 'SubsidiariesMember',
  'Associates': 'AssociatesMember',
  'JointVentures': 'JointVenturesWhereEntityIsVenturerMember',
  'KeyManagement': 'KeyManagementPersonnelOfEntityOrParentMember',
  'OtherRelatedParties': 'OtherRelatedPartiesMember',
  'Total': ''
};
const MEMBER_SHORT_NAMES = Object.keys(MEMBER_SUFFIX_MAP);

interface FormSchema {
  role: string;
  sheetId: string;
  label: string;
  description: string;
  columnSumValidations?: ColumnSumValidationConfig[];
  controls: SchemaControl[];
}

@Component({
  selector: 'app-role24-750000-notes-related-party-transactions',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    FieldLinkLabelComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputNumber,
    ButtonModule,
    Toast,
    SubmitForValidationComponent,
    RichTextFieldComponent,
    NegativeHighlightDirective,

    SchemaNumericDirective,
    SkeletonModule,
    PlaceholderPipe,
    FieldLinkTargetDirective
  ],
  providers: [MessageService],
  templateUrl: './role24-750000-notes-related-party-transactions.component.html',
  styleUrls: ['./role24-750000-notes-related-party-transactions.component.css']
})

export class Role24750000NotesRelatedPartyTransactionsComponent implements OnInit, OnDestroy {
  private stateService = inject(FilingFormStateService);
  private mappingStatus = inject(MappingStatusService);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private columnSumValidation = inject(ColumnSumValidationService);
  private crossRefService = inject(CrossReferenceService);
  private envService = inject(EnvironmentService);
  private destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();

  form!: FormGroup;
  schema?: FormSchema;
  /**
   * Card header carrying the filing's submission type, e.g.
   * "Notes - Related Party Transactions (FS-MPERS)".
   *
   * Two rules, both of which this sheet used to break:
   *  - the name comes from the canonical `SHEET_DISPLAY_NAMES` map (via
   *    `getSheetTitleWithSubmissionType`), never a literal repeated here;
   *  - it is a LIVE getter, never an `ngOnInit` snapshot — see the cold-mount
   *    rule on `FilingFormStateService.getFormTitleWithSubmissionType()`.
   */
  get formTitle(): string {
    return this.stateService.getSheetTitleWithSubmissionType(
      SHEET_KEYS.notesRelatedPartyTransactions
    );
  }

  get isDev(): boolean {
    return this.envService.isDevelopment();
  }

  /**
   * Column-visibility cells for this sheet, resolved via the shared
   * dimension-visibility foundation. Both this getter and the headless pure
   * validator feed the SAME `resolveDimensionVisibility()` resolver (through
   * `CrossReferenceService.getVisibility()` here, `resolveActiveDimensions()`
   * there), so the mounted and headless gates cannot drift.
   */
  get dimensionVisibility() {
    return this.crossRefService.getVisibility(SHEET_KEYS.notesRelatedPartyTransactions);
  }

  /**
   * Whether the Consolidated table should be displayed.
   * Returns true when the filing's Nature of Financial Statements is
   * 'Consolidated', false when 'Separate'. Mirrors role 23's
   * `showConsolidatedTable` — when false, only the Separate table renders and
   * only the Separate dimension is validated (the pure validator gates the same
   * way via resolveActiveDimensions()).
   */
  get showConsolidatedTable(): boolean {
    return isCellVisible(this.dimensionVisibility, 'Consolidated', 'CurrentYear');
  }

  /**
   * Formatted current financial-year date range for the dimension-table header
   * (e.g. "01/01/2024 - 31/12/2024"). Sourced from the Filing Information (Role-1)
   * DEI block via the shared CrossReferenceService — the single source of truth
   * used by Role 19 (SOCIE) so the period label stays consistent across sheets.
   * Role 24 is single-period, so only the current range is shown.
   */
  get periodHeaderCurrent(): string {
    return this.crossRefService.getFormattedFinancialYear('current');
  }

  /** Active consolidation dimensions for the current filing (Separate-only when
   * the Consolidated table is hidden). Used by validation + status loops. */
  private get activeDimensions(): ('Consolidated' | 'Separate')[] {
    return activeDimensions(this.dimensionVisibility) as ('Consolidated' | 'Separate')[];
  }

  isLoading = true;
  private isLoadingState = false;
  private isInitialLoadComplete = false;
  isValidationComplete = false;

  // Track which Level 4 sections are expanded (separate state for Consolidated and Separate)
  expandedSectionsConsolidated: { [key: string]: boolean } = {};
  expandedSectionsSeparate: { [key: string]: boolean } = {};

  /**
   * Section sinks for FieldLinkTargetDirective — write straight into the
   * records above (see FieldLinkSectionSink). One per table.
   */
  protected readonly consolidatedSectionSet: FieldLinkSectionSink = {
    add: (qname: string) => (this.expandedSectionsConsolidated[qname] = true),
  };
  protected readonly separateSectionSet: FieldLinkSectionSink = {
    add: (qname: string) => (this.expandedSectionsSeparate[qname] = true),
  };

  // Edit mode tracking per row (key: qname, value: original values)
  private editingRows = new Map<string, { [key: string]: number | null }>();

  /** Row Mapping-Status frozen at edit-enter; while a row is mid-edit its badge
   *  shows this committed value (not the live in-edit recompute) so the pencil
   *  alone never flips it to 'Edited'. Cleared on Tick/Cancel → badge recomputes.
   *  Key = getEditKey(control, dimension): `${qname}|${dimension}` */
  private frozenRowStatus = new Map<string, 'Failed' | 'Edited' | 'Validated*' | 'Validated'>();

  // Cell-level validation errors for Total column sum check
  private columnSumErrors = new Map<string, string>();

  // Row-level validation errors from column sum validation
  private validationErrors = new Map<string, string[]>();

  // ---- Performance caches (rebuilt on schema/state changes, not per CD tick) ----

  /**
   * Precomputed form-control names: key is `${rowQname}|${consolidation}|${member}`.
   * Built once in buildForm() so the template never has to rebuild the bracketed string.
   */
  private controlNameMap = new Map<string, string>();

  /**
   * Map of row qname -> the qname of its enclosing Level 4 SectionHeader (or null
   * if no Level 4 ancestor). Built once after schema load. Used to derive row visibility
   * from the expanded-section state cheaply.
   */
  private level4ParentByQname = new Map<string, string | null>();

  /**
   * Lazy cache of `isFieldRequired(control)` keyed by qname. Invalidated when the
   * cross-sheet `filingPrerequisites` data changes (see updateRequiredIfValidators
   * and setupCrossSheetValidationListener).
   */
  private requiredMap: Map<string, boolean> | null = null;

  /**
   * Lazy caches of "currently visible row qnames" per table. Invalidated whenever
   * a Level 4 section is toggled. The template reads `.has(qname)` instead of walking
   * the schema backwards on every change-detection tick.
   */
  private visibleConsolidated: Set<string> | null = null;
  private visibleSeparate: Set<string> | null = null;

  /**
   * Per-row cached validation status and pre-rendered error HTML. Both maps are
   * rebuilt by recomputeRowCaches() after validateColumnSum() runs, and after any
   * edit-mode operation that changes editingRows. Template bindings
   * read these via getRowValidationStatus() / getRowErrorDescription() so they stop
   * doing per-cell form.get() scans on every change-detection tick.
   */
  private rowStatusConsolidated = new Map<string, 'Failed' | 'Edited' | 'Validated*' | 'Validated'>();
  private rowStatusSeparate = new Map<string, 'Failed' | 'Edited' | 'Validated*' | 'Validated'>();
  private rowErrorConsolidated = new Map<string, string>();
  private rowErrorSeparate = new Map<string, string>();
  private rowHasErrorCategoryConsolidated = new Map<string, boolean>();
  private rowHasErrorCategorySeparate = new Map<string, boolean>();

  /**
   * Get total number of actual form fields (excluding section headers)
   */
  get totalFields(): number {
    if (!this.schema) return 0;
    return this.schema.controls.filter(control => control.type !== 'SectionHeader').length;
  }

  /** trackBy for the schema-driven *ngFor — stable identity keeps Angular from
   * destroying/rebuilding ~60 rows worth of DOM on each CD pass. */
  trackByQname = (_index: number, control: SchemaControl): string => control.qname;

  async ngOnInit(): Promise<void> {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '24',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    // Set loading flag to prevent form.valueChanges from marking sheet as edited during initialization
    this.isLoadingState = true;

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('========================================');
    // console.log('ROLE-24 COMPONENT LOADED - VERSION 2.0');
    // console.log('Standard Bracket Format (Standardized)');
    // console.log('========================================');

    try {
      this.stateService.markSheetInitializing(SHEET_KEYS.notesRelatedPartyTransactions);
      await this.loadSchema();
      this.initializeExpandedSections();
      this.buildForm();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
      this.loadSavedState();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
      this.setupStatePersistence();
      this.setupTextBlockPersistence();
      this.setupStateListener();
      this.setupCrossSheetValidationListener(); // Listen for requiredIf changes from other sheets
      this.setupRemoteUpdateAdoption();
      this.setupConditionalFieldLogic();
      this.markRequiredFieldsAsTouched(); // Mark required fields to show validation on load

      // Deterministic replacement for the legacy setTimeout(600 → 600)
      // finalization. Must complete BEFORE `isLoading` goes false: the flag it
      // closes (`isLoadingState`) gates both of this sheet's persist paths, so
      // clearing `isLoading` first would put the form on screen while user edits
      // were still being discarded. See finalizeInit's doc comment.
      await this.finalizeInit();

      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('✅ Role-24 initialization complete!');
    } catch (error) {
      console.error('❌ Role-24 initialization error:', error);
      this.isLoadingState = false; // Clear loading flag on error
      // Report through the trace instead of a fixed sentence. The toast now
      // names this sheet, the steps it reached, and whether the schema was
      // missing, unreadable, or fine but the form could not be built —
      // `classifySheetInitError` infers the last of those by elimination.
      // Must run BEFORE the loading flag is cleared below: `fail()` closes the
      // trace, which is what stops the finalize mark on the next line from
      // recording this failed init as a success.
      this.loadTraceHandle.fail(classifySheetInitError(error), error);
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Deterministic sheet-init finalization, replacing the legacy
   * setTimeout(600 → 600) chain.
   *
   * Why this sheet is TIER-A and passes no `awaitSettled`: the deferred TIER-B
   * note defines the test as "does an init calc flow through the DEBOUNCED
   * pipeline?". Here it cannot — every init write is `emitEvent: false`
   * (`loadSavedState`, the external-load patch), so `buildFormEditPipeline`
   * never fires during init and `validateColumnSum()` runs synchronously off
   * the direct call. The old chain was coalescing emissions that by
   * construction do not occur.
   *
   * NB: `persistInitial` is deliberately NOT passed. The legacy timer chain did
   * not re-baseline either, so omitting it keeps this conversion
   * behaviour-preserving. (role4/role6/role7 DO pass it, to re-baseline their
   * rich-text field's on-load fragment normalization — if a full revert on this
   * sheet is ever reported as "the pencil never clears", that asymmetry is the
   * first place to look. It is pre-existing, not introduced here.)
   */
  private async finalizeInit(): Promise<void> {
    await finalizeSheetInit({
      trace: this.loadTraceHandle,
      stateService: this.stateService,
      sheetKey: SHEET_KEYS.notesRelatedPartyTransactions,
      form: this.form,
      setLoadingState: (isLoading) => { this.isLoadingState = isLoading; },
      isComputed: computedControlPredicate(this.schema?.controls),
    });

    // This sheet publishes a RICHER verdict than the helper's `!form.valid`:
    // evaluateSheetStatus() rolls up per-row mapping status across the ACTIVE
    // dimensions as well as the non-table required fields. Re-publishing here
    // keeps it the last writer, exactly as the legacy chain had it.
    //
    // On the happy path this is inert — notesRelatedPartyTransactions is on
    // PURE_VERDICT_SHEETS, so updateSheetLevelStatus substitutes the pure
    // validator's booleans for whatever the component passed. It matters only in
    // the fail-safe branch where the pure validator throws and the component's
    // own verdict is retained; there, `!form.valid` alone would be wrong.
    this.evaluateSheetStatus();

    this.isInitialLoadComplete = true;
  }


  /**
   * Setup listener for external state changes (e.g., data loaded from backend)
   * This allows the form to react when the container populates data via the state service
   */
  private setupStateListener(): void {
    this.stateService.state$
      .pipe(
        map((state) => state.notesRelatedPartyTransactions),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((notesRelatedPartyTransactionsData) => {
        if (notesRelatedPartyTransactionsData && Object.keys(notesRelatedPartyTransactionsData).length > 0) {
          // After initial load is complete, skip re-patching and re-snapshotting
          // when the state change came from the user's own edits (via saveEditMode → updateSheetData).
          // Re-snapshotting the baseline would overwrite the original state with the user's edits,
          // making isSheetModified() return false and keeping the buttons disabled.
          if (this.isInitialLoadComplete) {
            return;
          }

          // Bracket the external load so the patch below cannot be mistaken for a
          // user edit. Opened and closed synchronously — see the note after the patch.
          this.isLoadingState = true;

          // NO transformation needed - data is already transformed in filing-form-container
          // Patch the form with the data (already in frontend format)
          this.form.patchValue(notesRelatedPartyTransactionsData, { emitEvent: false });

          // Run column sum validation after data load
          this.validateColumnSum();

          // Finalize synchronously, replacing a legacy setTimeout(700 → 600).
          // The patch above is `emitEvent: false` and validateColumnSum() is
          // synchronous, so the debounced edit pipeline never fires on this path —
          // there is no pending emission for a timer to wait out. Deferring only
          // prolonged the window in which `isLoadingState` silently DROPS a user
          // edit: it gates `shouldSkip` on the debounced pipeline AND the
          // early-return in setupTextBlockPersistence, and the form is already on
          // screen by the time external data arrives.
          this.isLoadingState = false;
          this.evaluateSheetStatus();
          this.stateService.markSheetInitialized(SHEET_KEYS.notesRelatedPartyTransactions);
        }
      });
  }

  /**
   * Cross-tab safety net — sibling-tab edits to this sheet's own state must
   * repaint here too. This is a separate concern from `setupCrossSheetValidationListener`
   * above (which watches filingPrerequisites for requiredIf conditions, not this
   * sheet's own slice).
   */
  private setupRemoteUpdateAdoption(): void {
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.notesRelatedPartyTransactions)),
        filter(() => !!this.schema && !!this.form),
        takeUntil(this.destroy$)
      )
      .subscribe(({ sheetKey, changedSheets }) => {
        if (this.envService.isCrossTabSyncDebugEnabled()) {
          // console.log('[CrossTabSync][remote-validation-triggered]', {
          //   receiver: 'role24-notesRelatedPartyTransactions',
          //   sourceSheetKey: sheetKey,
          // });
        }

        if (changedSheets.includes(SHEET_KEYS.notesRelatedPartyTransactions))
          this.adoptRemoteState();

        this.evaluateSheetStatus();
      });
  }

  /**
   * Cross-tab adoption for role24 (notes related party transactions). Rows
   * currently open in edit mode are skipped — the user's in-flight work wins
   * until they tick (local overwrites and broadcasts) or press X (the row
   * lands on the adopted value; see `cancelEditMode`).
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoInlineEditSheet`.
   * `validateColumnSum()` here recomputes for DISPLAY only; the sibling already
   * persisted its own recomputed totals.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    const openControlNames = [...this.editingRows.values()]
      .flatMap(snapshot => Object.keys(snapshot ?? {}));

    adoptIntoInlineEditSheet(
      this.form,
      this.stateService.getSheetData(SHEET_KEYS.notesRelatedPartyTransactions),
      openControlNames
    );

    this.validateColumnSum();
  }

  /**
   * Setup listener for cross-sheet validation (requiredIf conditions)
   * Monitors changes in filingPrerequisites to update validators dynamically
   */
  private setupCrossSheetValidationListener(): void {
    // Listen for changes in filingPrerequisites sheet
    this.stateService.state$
      .pipe(
        // BOTH filing-info slots. Role 0 (`filingPrerequisites`) is the editable
        // slot in Phase 1 ONLY; Phase 2 writes to role 1 (`filingInfo`) and
        // leaves the role-0 slot an empty stub, so watching role 0 alone never
        // fired here. Guarded by `npm run check:filing-info-watch`.
        map((state) => ({
          filingPrerequisites: state.filingPrerequisites,
          filingInfo: state.filingInfo,
        })),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Re-evaluate requiredIf conditions and update validators
        this.updateRequiredIfValidators();
      });
  }

  /**
   * Update validators for fields with requiredIf conditions
   * Called when the dependent sheet data changes
   */
  private updateRequiredIfValidators(): void {
    if (!this.schema) return;

    // Invalidate the requiredMap cache — dependent sheet changed, so isFieldRequired may flip.
    this.requiredMap = null;

    this.schema.controls.forEach(control => {
      // Only process fields with requiredIf condition
      if (!control.requiredIf) return;

      const isRequired = this.isFieldRequired(control);

      // For table column fields, update all 16 form controls
      if (control.ui.tableColumn) {
        const members = ['Parent', 'JointControl', 'Subsidiaries', 'Associates', 'JointVentures', 'KeyManagement', 'OtherRelatedParties', 'Total'];
        const consolidationTypes: ('Consolidated' | 'Separate')[] = ['Consolidated', 'Separate'];

        consolidationTypes.forEach(consolidationType => {
          members.forEach(member => {
            const controlName = this.getXbrlControlName(control.qname, consolidationType, member);
            const formControl = this.form.get(controlName);
            if (formControl) {
              if (isRequired) {
                formControl.setValidators([Validators.required]);
              } else {
                formControl.clearValidators();
              }
              // { onlySelf: true } avoids an O(N) parent-group revalidation per call.
              // We run one form-level updateValueAndValidity at the end.
              formControl.updateValueAndValidity({ emitEvent: false, onlySelf: true });
            }
          });
        });
      } else {
        // For non-table fields
        const formControl = this.form.get(control.qname);
        if (formControl) {
          if (isRequired) {
            formControl.setValidators([Validators.required]);
          } else {
            formControl.clearValidators();
          }
          formControl.updateValueAndValidity({ emitEvent: false, onlySelf: true });
        }
      }
    });

    // Single parent-group validity recompute after all child validators have been swapped.
    this.form?.updateValueAndValidity({ emitEvent: false });

    // Required-state flip changes row statuses / error descriptions → refresh caches.
    this.recomputeRowCaches();
  }

  /**
   * Initialize all Level 4 sections as expanded by default (for both Consolidated and Separate)
   */
  private initializeExpandedSections(): void {
    if (!this.schema) return;

    this.schema.controls.forEach(control => {
      if (control.type === 'SectionHeader' && control.ui.level === 4) {
        // Use qname as unique identifier for each section
        this.expandedSectionsConsolidated[control.qname] = true; // All expanded by default
        this.expandedSectionsSeparate[control.qname] = true; // All expanded by default
      }
    });
  }

  /**
   * Toggle expand/collapse state for a Level 4 section in Consolidated table
   */
  toggleSectionConsolidated(sectionQname: string): void {
    this.expandedSectionsConsolidated[sectionQname] = !this.expandedSectionsConsolidated[sectionQname];
    this.visibleConsolidated = null; // invalidate cache — rebuilt lazily on next read
  }

  /**
   * Toggle expand/collapse state for a Level 4 section in Separate table
   */
  toggleSectionSeparate(sectionQname: string): void {
    this.expandedSectionsSeparate[sectionQname] = !this.expandedSectionsSeparate[sectionQname];
    this.visibleSeparate = null;
  }

  /**
   * Build a Set of qnames that are currently visible in the given table, based on
   * the expanded/collapsed state of their enclosing Level 4 section. Replaces the
   * per-render backward-walk through the schema.
   */
  private buildVisibleRowSet(expanded: { [key: string]: boolean }): Set<string> {
    const visible = new Set<string>();
    this.level4ParentByQname.forEach((parentQname, rowQname) => {
      // No Level-4 ancestor → always visible. Otherwise honour the expanded flag
      // (missing key defaults to expanded, matching the original isSectionExpanded* logic).
      if (parentQname === null || expanded[parentQname] !== false) {
        visible.add(rowQname);
      }
    });
    return visible;
  }

  /**
   * Check if a Consolidated section is expanded
   */
  isSectionExpandedConsolidated(sectionQname: string): boolean {
    return this.expandedSectionsConsolidated[sectionQname] !== false; // Default to true if not set
  }

  /**
   * Check if a Separate section is expanded
   */
  isSectionExpandedSeparate(sectionQname: string): boolean {
    return this.expandedSectionsSeparate[sectionQname] !== false; // Default to true if not set
  }

  /**
   * Check if a row belongs to a Level 4 section and should be shown (Consolidated table)
   */
  shouldShowRowConsolidated(control: SchemaControl, _index: number): boolean {
    if (!control.ui.tableColumn) return true;
    if (!this.visibleConsolidated) {
      this.visibleConsolidated = this.buildVisibleRowSet(this.expandedSectionsConsolidated);
    }
    return this.visibleConsolidated.has(control.qname);
  }

  /**
   * Check if a row belongs to a Level 4 section and should be shown (Separate table)
   */
  shouldShowRowSeparate(control: SchemaControl, _index: number): boolean {
    if (!control.ui.tableColumn) return true;
    if (!this.visibleSeparate) {
      this.visibleSeparate = this.buildVisibleRowSet(this.expandedSectionsSeparate);
    }
    return this.visibleSeparate.has(control.qname);
  }

  /**
   * Load schema from JSON file (dynamically selects MFRS or MPERS)
   */
  private async loadSchema(): Promise<void> {
    // Wait for retrieve-mbrs-data hydration so the accounting standard is
    // populated before schema selection — a mid-hydration refresh mount would
    // otherwise fall back to the MFRS default and MPERS values would never bind
    // (blank form).
    await this.stateService.waitForMbrsHydration('role24');
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.hydrationSettled);

    // Get the accounting standard from filing info
    const filingData = this.stateService.getActiveFilingInfoData();
    const accountingStandard = filingData['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements'] || '';

    // Delegate schema-variant selection to the shared rules module so the
    // pure validator and this component resolve identically. See
    // role24-notes-related-party-transactions.rules.ts.
    const schemaFile = pickRole24SchemaFile(accountingStandard);

    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
    this.schema = await fetchSheetSchema<FormSchema>(
      this.loadTraceHandle,
      `/assets/forms/${schemaFile}`,
    );
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);
  }

  /**
   * Build reactive form from schema
   */
  private buildForm(): void {
    const formControls: any = {};

    // Build the row -> Level-4-parent map now so shouldShowRow* can do O(1) lookups.
    this.level4ParentByQname.clear();
    this.controlNameMap.clear();
    if (this.schema?.controls) {
      let currentLevel4: string | null = null;
      for (const control of this.schema.controls) {
        if (control.type === 'SectionHeader' && control.ui.level === 4) {
          currentLevel4 = control.qname;
          continue;
        }
        // A lower-level header (Level 2/3) resets the current Level 4 scope.
        if (control.type === 'SectionHeader' && control.ui.level && control.ui.level < 4) {
          currentLevel4 = null;
          continue;
        }
        if (control.ui.tableColumn) {
          this.level4ParentByQname.set(control.qname, currentLevel4);
        }
      }
    }

    if (this.schema && this.schema.controls) {
      this.schema.controls.forEach(control => {
        if (control.type === 'SectionHeader') {
          return; // Skip headers
        }

        // TextBlock fields remain as single controls
        if (control.type === 'TextBlock') {
          const defaultValue = this.getDefaultValue(control);
          const validators: any[] = this.getValidators(control);
          formControls[control.qname] = new FormControl(defaultValue, validators);
          return;
        }

        // Table column fields get 16 controls each using STANDARD BRACKET FORMAT
        // Pattern: {qname}:member[Consolidated|Separate][CurrentYear]
        // Role 24 only uses CurrentYear (no PreviousYear)
        if (control.ui.tableColumn) {
          const defaultValue = this.getDefaultValue(control);
          const validators: any[] = this.getValidators(control);

          // All controls start as disabled by default (edit mode will enable them)
          const startsDisabled = true;

          // Related party members (NO namespace prefixes needed)
          // Backend provides full qname with member (e.g., "ifrs-full:Revenue:ParentMember")
          // Transformer service preserves this and adds dimensional brackets.
          // Sourced from role24-notes-related-party-transactions.rules so the
          // pure validator's buildRole24RowColumnKeys() and this expansion
          // stay in lockstep.
          const members = ROLE24_MEMBER_SUFFIXES;

          // Create 16 FormControls (8 members × 2 dimensions, CurrentYear only)
          members.forEach(member => {
            const baseKey = member ? `${control.qname}:${member}` : control.qname;

            const consolidatedName = `${baseKey}[Consolidated][CurrentYear]`;
            const separateName = `${baseKey}[Separate][CurrentYear]`;

            formControls[consolidatedName] = new FormControl(
              { value: defaultValue, disabled: startsDisabled },
              validators
            );
            formControls[separateName] = new FormControl(
              { value: defaultValue, disabled: startsDisabled },
              validators
            );
          });

          // Populate controlNameMap using the public short-name keys used by templates
          // (e.g. "Parent" → "<qname>:ParentMember[...]"). This lets getXbrlControlName()
          // be a pure Map lookup in the hot path.
          MEMBER_SHORT_NAMES.forEach(shortName => {
            const suffix = MEMBER_SUFFIX_MAP[shortName];
            const base = suffix ? `${control.qname}:${suffix}` : control.qname;
            this.controlNameMap.set(
              `${control.qname}|Consolidated|${shortName}`,
              `${base}[Consolidated][CurrentYear]`
            );
            this.controlNameMap.set(
              `${control.qname}|Separate|${shortName}`,
              `${base}[Separate][CurrentYear]`
            );
          });
        } else {
          // Other fields remain as single controls
          const defaultValue = this.getDefaultValue(control);
          const validators: any[] = this.getValidators(control);
          formControls[control.qname] = new FormControl(defaultValue, validators);
        }
      });
    }

    this.form = new FormGroup(formControls);
  }

  /**
   * Get default value for control based on type
   */
  private getDefaultValue(control: SchemaControl): any {
    switch (control.type) {
      case 'YesNoItemType':
        return null;
      case 'IntegerItemType':
      case 'MonetaryItemType':
        return null;
      case 'TextBlock':
      case 'StringItemType':
      default:
        return '';
    }
  }

  /**
   * Check if a field is required based on both `required` and `requiredIf` conditions
   * For requiredIf, checks the value from another sheet (cross-sheet validation)
   */
  isFieldRequired(control: SchemaControl): boolean {
    // Hot path: memoized result keyed by qname. Cache is invalidated whenever
    // the dependent sheet changes (see updateRequiredIfValidators).
    if (!this.requiredMap) {
      this.rebuildRequiredMap();
    }
    const cached = this.requiredMap!.get(control.qname);
    if (cached !== undefined) return cached;

    // Fallback for controls added after the cache was built.
    const result = this.computeFieldRequired(control);
    this.requiredMap!.set(control.qname, result);
    return result;
  }

  /**
   * Compute whether a control is required. Pulled out of isFieldRequired so the
   * cache-miss path and the rebuild path share one implementation.
   */
  private computeFieldRequired(control: SchemaControl): boolean {
    // Single-sourced through `isControlMandatory`. Note this sheet spells its
    // cross-sheet `requiredIf` target `qname` while role 12 spells the same
    // thing `field`; the util accepts both, which is why the polymorphic shape
    // no longer has to be re-learned per sheet.
    return isControlMandatory(control as any, {
      // `filingPrerequisites` resolves through the SAME rule the column-visibility
      // resolver uses (`resolvePrerequisiteValue`: prereq-first, then filingInfo).
      // A bare getSheetData('filingPrerequisites') was silently dead here: since
      // the role0/role1 phase split, role 0 is the editable filing-info slot in
      // Phase 1 ONLY, and this is a Phase-2-only sheet, so `TypeOfCompany` was
      // always undefined and `AmountsPayableRelatedPartyTransactions` never
      // became mandatory. The headless validator was unaffected —
      // `buildSheetValidationContext` aliases the active filing-info under BOTH
      // state keys. Same fix as role 12's `validateConditionalMandatoryFields`.
      crossSheetValue: (sheet, field) =>
        sheet === SHEET_KEYS.filingPrerequisites
          ? resolvePrerequisiteValue(field, {
              filingPrerequisites: this.stateService.getSheetData(
                SHEET_KEYS.filingPrerequisites
              ),
              filingInfo: this.stateService.getSheetData(SHEET_KEYS.filingInfo),
            })
          : (this.stateService.getSheetData(sheet as any) as any)?.[field],
    });
  }

  /**
   * Rebuild the requiredMap cache. Cheap — one pass over schema controls.
   * Reads cross-sheet state once at the top instead of once per table cell.
   */
  private rebuildRequiredMap(): void {
    this.requiredMap = new Map<string, boolean>();
    if (!this.schema) return;
    for (const control of this.schema.controls) {
      this.requiredMap.set(control.qname, this.computeFieldRequired(control));
    }
  }

  /**
   * Get validators for control
   */
  private getValidators(control: SchemaControl): any[] {
    const validators: any[] = [];

    // Add required validator if control is required (either directly or via requiredIf)
    if (this.isFieldRequired(control)) {
      validators.push(Validators.required);
    }

    return validators;
  }

  /**
   * Reveal the sheet's already-computed errors on load.
   *
   * Replaces a bespoke implementation that walked the schema, expanded each
   * `tableColumn` control into its 16 member/dimension control names by hand,
   * and revealed only fields `isFieldRequired` agreed were mandatory. Three
   * things came out of that rewrite:
   *
   * - it revealed `required` errors only, so a min/pattern error on a share
   *   figure stayed invisible until the preparer touched that exact cell;
   * - it also called `markAsDirty()`, which claims the preparer edited a field
   *   they have not seen — the reveal is a display concern, not an edit;
   * - the hand-maintained member list had to track `buildForm`'s control naming,
   *   a duplication the shared util avoids by iterating `form.controls`.
   */
  private markRequiredFieldsAsTouched(): void {
    revealValidationErrors(this.form, {
      isComputed: computedControlPredicate(this.schema?.controls),
    });
  }

  /**
   * Get minimum value from control validators (for UI binding)
   */
  getMinValue(control: SchemaControl): number | undefined {
    if (!control.validators) return undefined;

    const minValidator = control.validators.find(validator => validator.name === 'min');
    if (!minValidator) return undefined;

    // Support both "value" and "min" properties
    return (minValidator as any).value !== undefined
      ? (minValidator as any).value
      : (minValidator as any).min;
  }

  /**
   * Setup conditional field logic
   */
  private setupConditionalFieldLogic(): void {
    if (!this.schema) return;

    this.schema.controls.forEach(control => {
      if (control.ui.conditionalOn) {
        const parentControl = this.form.get(control.ui.conditionalOn);
        const childControl = this.form.get(control.qname);

        if (parentControl && childControl) {
          // Update child visibility based on parent value
          parentControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(value => {
              // No need to disable/enable - template handles visibility
            });
        }
      }
    });
  }

  /**
   * Validate column sum: Total should equal sum of all component columns
   * Uses ColumnSumValidationService to check if Total column equals the sum of component columns.
   * This validation is per row (each line item is treated separately)
   */
  private validateColumnSum(): void {
    // Clear previous errors
    this.columnSumErrors.clear();

    // Skip if no schema or no columnSumValidations defined
    if (!this.schema || !this.schema.columnSumValidations || this.schema.columnSumValidations.length === 0) {
      return;
    }

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('🔍 Starting column sum validation for Role 24');

    // Get row qnames (tableColumn controls)
    const rowQnames = this.schema.controls
      .filter(control => control.ui.tableColumn && control.type !== 'SectionHeader')
      .map(control => control.qname);

    // Only validate the dimensions the editor actually renders. When the filing
    // is "Separate" the Consolidated table is hidden, so its (possibly stale)
    // cells must not raise column-sum errors the user can no longer fix.
    const consolidationTypes = this.activeDimensions;

    // Get form values (including disabled controls)
    const formValues = this.form.getRawValue();

    // Aggregate all cell errors and row errors from all validation configs
    const allCellErrors = new Map<string, string>();
    const allRowErrors = new Map<string, string[]>();

    // Process each validation config from schema
    this.schema.columnSumValidations.forEach(config => {
      const { cellErrors, rowErrors } = this.columnSumValidation.validateColumnSum(
        formValues,
        rowQnames,
        consolidationTypes,
        [''], // Role 24 doesn't have period types, use empty string as placeholder
        (rowQname, columnQname, consolidationType, periodType) =>
          this.getRole24ValidationControlName(rowQname, columnQname, consolidationType, periodType),
        config
      );

      // Merge cell errors
      cellErrors.forEach((error, key) => {
        allCellErrors.set(key, error);
      });

      // Merge row errors
      rowErrors.forEach((errors, rowQname) => {
        const existing = allRowErrors.get(rowQname) || [];
        allRowErrors.set(rowQname, [...existing, ...errors]);
      });
    });

    // Store cell-level errors for red border
    this.columnSumErrors = allCellErrors;

    // Update validationErrors Map with row errors
    rowQnames.forEach(rowQname => {
      const newErrors = allRowErrors.get(rowQname) || [];
      const existingErrors = this.validationErrors.get(rowQname) || [];

      // Filter out old column sum validation errors (based on all subTotalLabels in config)
      const subTotalLabels = this.schema!.columnSumValidations!.map(validation => validation.subTotalLabel);
      const otherErrors = existingErrors.filter(
        error => !subTotalLabels.some(label => error.includes(label))
      );

      if (newErrors.length > 0) {
        // Combine with new errors
        const combinedErrors = [...otherErrors, ...newErrors];
        this.validationErrors.set(rowQname, combinedErrors);
      } else {
        // Remove column sum errors if they exist, keep other errors
        if (otherErrors.length > 0) {
          this.validationErrors.set(rowQname, otherErrors);
        } else if (existingErrors.length > 0) {
          this.validationErrors.delete(rowQname);
        }
      }
    });

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`✅ Column sum validation complete (Role 24) - ${this.columnSumErrors.size} cell(s) with errors`);

    // Refresh per-row view caches so the template doesn't recompute these on every
    // change-detection tick.
    this.recomputeRowCaches();
  }

  /**
   * Rebuild row-level UI caches (status, error HTML, error-category flag) for every
   * tableColumn row in both Consolidated and Separate tables. Called after
   * validateColumnSum() and after edit-mode operations that change editingRows.
   *
   * These caches exist only to avoid the template re-running ~8 form.get() calls and
   * ~20 string concatenations per row per CD tick. The results are identical to the
   * direct computeRow* methods, so accuracy parity is preserved.
   */
  private recomputeRowCaches(): void {
    this.rowStatusConsolidated.clear();
    this.rowStatusSeparate.clear();
    this.rowErrorConsolidated.clear();
    this.rowErrorSeparate.clear();
    this.rowHasErrorCategoryConsolidated.clear();
    this.rowHasErrorCategorySeparate.clear();

    if (!this.schema?.controls) return;

    for (const control of this.schema.controls) {
      if (!control.ui.tableColumn) continue;

      this.rowStatusConsolidated.set(control.qname, this.computeRowValidationStatus(control, 'Consolidated'));
      this.rowStatusSeparate.set(control.qname, this.computeRowValidationStatus(control, 'Separate'));
      this.rowErrorConsolidated.set(control.qname, this.computeRowErrorDescription(control, 'Consolidated'));
      this.rowErrorSeparate.set(control.qname, this.computeRowErrorDescription(control, 'Separate'));
      this.rowHasErrorCategoryConsolidated.set(control.qname, this.computeRowHasErrorCategory(control, 'Consolidated'));
      this.rowHasErrorCategorySeparate.set(control.qname, this.computeRowHasErrorCategory(control, 'Separate'));
    }
  }

  /**
   * Get control name for Role 24's validation
   * Maps column qnames from schema's columnSumValidations to form control names
   *
   * Schema uses: "Total", "ifrs-full:ParentMember", "ssmt-mpers:ParentMember", etc.
   * Form controls use: "{qname}[Consolidated][CurrentYear]" (Total),
   *                    "{qname}:ParentMember[Consolidated][CurrentYear]" (Parent), etc.
   *
   * @param rowQname - The row's base qname
   * @param consolidationType - 'Consolidated' or 'Separate'
   * @param columnQname - The column qname from schema (e.g., "Total", "ifrs-full:ParentMember")
   * @param _periodType - Not used in Role 24 (placeholder for compatibility)
   * @returns Form control name matching the buildForm pattern
   */
  private getRole24ValidationControlName(
    rowQname: string,
    columnQname: string,
    consolidationType: string,
    _periodType: string
  ): string {
    if (columnQname === 'Total') {
      // Total: no member suffix, uses base qname
      return `${rowQname}[${consolidationType}][CurrentYear]`;
    }

    // Strip namespace prefix (e.g., "ifrs-full:ParentMember" → "ParentMember")
    const memberName = columnQname.includes(':') ? columnQname.split(':')[1] : columnQname;

    return `${rowQname}:${memberName}[${consolidationType}][CurrentYear]`;
  }

  /**
   * Check if a specific cell has a column sum validation error
   * Used to add red border to individual input fields
   */
  hasColumnSumError(control: SchemaControl, consolidatedOrSeparate: 'Consolidated' | 'Separate', relatedPartyMember: string): boolean {
    // Check if this column is a sub-total column defined in schema's columnSumValidations
    if (!this.schema?.columnSumValidations || this.schema.columnSumValidations.length === 0) {
      return false;
    }

    // Check if the relatedPartyMember matches any subTotalColumnQname in the validation configs
    const isSubTotalColumn = this.schema.columnSumValidations.some(
      config => config.subTotalColumnQname === relatedPartyMember
    );
    if (!isSubTotalColumn) return false;

    // Use the same control name format as validation
    const controlName = this.getRole24ValidationControlName(control.qname, relatedPartyMember, consolidatedOrSeparate, '');
    return this.columnSumErrors.has(controlName);
  }

  /**
   * Check if a specific cell is invalid (for cell background styling)
   * Used to add light red background to table cells for mandatory fields
   * Note: Returns false when the row is in edit mode to avoid red background during editing
   */
  isCellInvalid(control: SchemaControl, consolidatedOrSeparate: 'Consolidated' | 'Separate', relatedPartyMember: string): boolean {
    // Don't show red background when this dimension's row is in edit mode
    if (this.isRowInEditMode(control, consolidatedOrSeparate)) return false;

    // Use isFieldRequired to check both required and requiredIf conditions
    if (!this.isFieldRequired(control)) return false;

    const controlName = this.getXbrlControlName(control.qname, consolidatedOrSeparate, relatedPartyMember);
    const formControl = this.form.get(controlName);

    if (!formControl) return false;

    // Check if the control is invalid and has been touched/dirty
    return formControl.invalid && (formControl.touched || formControl.dirty);
  }

  /**
   * Check if an input should have red border (for required field validation)
   * Used to add red border to input fields when they are empty
   * Note: Works regardless of edit mode - shows red border when empty, removes when filled
   */
  isInputInvalid(control: SchemaControl, consolidatedOrSeparate: 'Consolidated' | 'Separate', relatedPartyMember: string): boolean {
    // Use isFieldRequired to check both required and requiredIf conditions
    if (!this.isFieldRequired(control)) return false;

    const controlName = this.getXbrlControlName(control.qname, consolidatedOrSeparate, relatedPartyMember);
    const formControl = this.form.get(controlName);

    if (!formControl) return false;

    // Check if the value is empty (null, undefined, or empty string)
    const value = formControl.value;
    return value === null || value === undefined || value === '';
  }

  /**
   * Check if field should be displayed based on conditional logic
   */
  shouldDisplayField(control: SchemaControl): boolean {
    if (!control.ui.conditionalOn) {
      return true;
    }

    const parentControl = this.form?.get(control.ui.conditionalOn);
    if (!parentControl) {
      return true;
    }

    return parentControl.value === control.ui.conditionalValue;
  }

  /**
   * Load saved state from service
   */
  private loadSavedState(): void {
    const savedData = this.stateService.getSheetData(SHEET_KEYS.notesRelatedPartyTransactions);
    if (savedData && Object.keys(savedData).length > 0) {
      this.form.patchValue(savedData, { emitEvent: false });

      // Run column sum validation after loading
      this.validateColumnSum();
    }

    // The gate enterEditMode() checks before letting a row open. Set
    // synchronously in BOTH branches, as the pair of legacy setTimeout(100)s
    // did: the patch above is `emitEvent: false` and validateColumnSum() is
    // synchronous, so nothing is outstanding to wait for — the delay only
    // postponed the moment the preparer could click the pencil.
    this.isValidationComplete = true;
  }

  /**
   * Setup automatic state persistence
   *
   * Validation + UI caches run synchronously on every form change so the row
   * status / error description react instantly to user input. State persistence
   * (localStorage, stateService) stays debounced to avoid write amplification.
   */
  private setupStatePersistence(): void {
    // Synchronous: keep UI caches fresh on every form change so row status / error
    // description react instantly (same-sheet feedback, live during edit).
    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.isLoadingState) {
          this.validateColumnSum(); // also triggers recomputeRowCaches()
        }
      });

    // Live edit-mode cycle: same-sheet validation only, NO persist (commit happens
    // on Tick / rich-text Save). Rich-text TextBlock commits are persisted by their
    // own handler (setupTextBlockPersistence).
    buildEditModeLivePipeline({
      source$: this.form.valueChanges,
      debounceMs: FORM_AUTOSAVE_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      shouldSkip: () => this.isLoadingState,
      validateSameSheet: () => this.validateColumnSum(),
      evaluateStatus: () => this.evaluateSheetStatus(),
    });
  }

  /**
   * Persist TextBlock (rich-text) fields on their own valueChanges. A rich-text
   * field only emits when the user clicks Save in its popup, so each emission is an
   * explicit commit. We subset-persist just that field's qname so a concurrently-
   * mid-edit numeric cell can never ride along.
   */
  private setupTextBlockPersistence(): void {
    if (!this.schema) return;
    for (const control of this.schema.controls) {
      if (control.type !== 'TextBlock') continue;
      const fc = this.form.get(control.qname);
      if (!fc) continue;
      const qname = control.qname;
      fc.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (this.isLoadingState) return;
          this.stateService.updateSheetData(
            SHEET_KEYS.notesRelatedPartyTransactions,
            pickSubset(this.form.getRawValue(), [qname]),
          );
          this.evaluateSheetStatus();
        });
    }
  }

  /**
   * Auto-fill demo data
   */
  autoFillDemo(): void {
    if (!this.schema) return;

    const demoData: any = {};

    // Fill TextBlock fields with sample content
    for (const control of this.schema.controls) {
      if (control.type === 'TextBlock') {
        const cleanLabel = control.label.replace(' [text block]', '');
        demoData[control.qname] = `This is sample disclosure content for ${cleanLabel}.\n\nThe note would contain detailed information about ${cleanLabel.toLowerCase()}.\n\nThis content is auto-filled for demonstration purposes only.`;
      } else if (control.type === 'YesNoItemType') {
        demoData[control.qname] = 'Yes';
      } else if (control.type === 'MonetaryItemType') {
        demoData[control.qname] = Math.floor(Math.random() * 1000000);
      }
    }

    this.form.patchValue(demoData);

    this.messageService.add({
      severity: 'success',
      summary: 'Demo Data Loaded',
      detail: 'Filled form with sample data',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Clear form
   */
  clearForm(): void {
    if (confirm('Are you sure you want to clear all data in this form?')) {
      this.form.reset();
      this.stateService.clearSheetData(SHEET_KEYS.notesRelatedPartyTransactions);

      this.messageService.add({
        severity: 'info',
        summary: 'Form Cleared',
        detail: 'All form data has been cleared',
        life: TOAST_DURATION_DEFAULT_MS
      });
    }
  }

  /**
   * Submit form
   */
  submitForm(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields correctly',
        life: TOAST_DURATION_DEFAULT_MS
      });
      return;
    }

    const formData = this.form.value;

    this.messageService.add({
      severity: 'success',
      summary: 'Form Submitted',
      detail: 'Related party transactions note has been saved successfully',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Get form control by qname
   */
  getControl(qname: string): any {
    return this.form.get(qname);
  }

  /**
   * Check if a control is inside the table section
   * (Used to determine if Level 2 headers should be rendered inside or outside the table)
   */
  isInsideTableSection(control: SchemaControl): boolean {
    if (!this.schema) return false;

    // Find the index of this control
    const currentIndex = this.schema.controls.findIndex(candidate => candidate.qname === control.qname);
    if (currentIndex === -1) return false;

    // Check if this is the "Disclosure of transactions between related parties" Level 2 header
    // This header should be inside the table
    if (control.type === 'SectionHeader' &&
        control.ui.level === 2 &&
        control.label === 'Disclosure of transactions between related parties') {
      return true;
    }

    return false;
  }

  /**
   * Generate form control key in STANDARD BRACKET FORMAT
   * Format: {qname}:member[Consolidated|Separate][CurrentYear]
   * For Total: {qname}[Consolidated|Separate][CurrentYear] (no member suffix)
   *
   * This matches the standardized bracket format used across all roles.
   * Role 24 only uses CurrentYear (no PreviousYear data).
   *
   * Note: Member names do NOT include namespace prefixes. The backend provides
   * full qnames like "ifrs-full:Revenue:ParentMember" already.
   *
   * @param qname - The XBRL qname
   * @param consolidatedOrSeparate - Dimension (Consolidated or Separate)
   * @param relatedPartyMember - Related party identifier (e.g., 'Parent', 'Total')
   * @returns Bracket format key for form control access
   */
  getXbrlControlName(qname: string, consolidatedOrSeparate: 'Consolidated' | 'Separate', relatedPartyMember: string): string {
    const cacheKey = `${qname}|${consolidatedOrSeparate}|${relatedPartyMember}`;
    const cached = this.controlNameMap.get(cacheKey);
    if (cached !== undefined) return cached;

    // Fallback (only reached before buildForm has populated the map).
    const memberSuffix = MEMBER_SUFFIX_MAP[relatedPartyMember] ?? '';
    const baseKey = memberSuffix ? `${qname}:${memberSuffix}` : qname;
    const name = `${baseKey}[${consolidatedOrSeparate}][CurrentYear]`;
    this.controlNameMap.set(cacheKey, name);
    return name;
  }

  /**
   * Get combined validation status for a row (checking all 8 input columns including Total)
   * Priority: Edited (deviates from last submission or in edit mode) > Failed > Validated* > Validated
   */
  getRowValidationStatus(control: SchemaControl, consolidatedOrSeparate: 'Consolidated' | 'Separate'): 'Failed' | 'Edited' | 'Validated*' | 'Validated' {
    if (!control.ui.tableColumn) return 'Validated';
    if (this.isRowInEditMode(control, consolidatedOrSeparate)) {
      const frozen = this.frozenRowStatus.get(this.getEditKey(control, consolidatedOrSeparate));
      if (frozen !== undefined) return frozen;
    }
    const cache = consolidatedOrSeparate === 'Consolidated' ? this.rowStatusConsolidated : this.rowStatusSeparate;
    const cached = cache.get(control.qname);
    return cached ?? 'Validated';
  }

  /**
   * Actual status calculation (moved here so recomputeRowCaches() can cheaply rebuild
   * the Map without the template ever hitting this code path).
   * Delegates to MappingStatusService.computeRowStatus() for priority logic.
   */
  private computeRowValidationStatus(
    control: SchemaControl,
    consolidatedOrSeparate: 'Consolidated' | 'Separate'
  ): 'Failed' | 'Edited' | 'Validated*' | 'Validated' {
    if (!control.ui.tableColumn) return 'Validated';

    const inputMembers = MEMBER_SHORT_NAMES;

    const controlNames = inputMembers.map(member =>
      this.getXbrlControlName(control.qname, consolidatedOrSeparate, member)
    );

    const currentValues = this.form.getRawValue() as Record<string, unknown>;

    // VERDICT signal — the raw blocking-error check, NOT masked by the 'Edited'
    // display state applied via forceEdited below. See rowHasBlockingError().
    const hasError = this.rowHasBlockingError(control, consolidatedOrSeparate);

    // Role24 has no Validated* / suggested-change warnings (hasSuggestedChangeCategory always false)
    const hasSuggestedChangeWarning = false;

    return this.mappingStatus.computeRowStatus({
      controlNames,
      currentValues,
      lastSubmissionValues: this.mappingStatus.getLastSubmissionValues(SHEET_KEYS.notesRelatedPartyTransactions),
      hasError,
      hasSuggestedChangeWarning,
      forceEdited: false,
    });
  }

  /**
   * Whether a table row carries a real blocking error (invalid cell, empty
   * required cell, or a column-sum mismatch) for the given dimension — the
   * sheet-level VERDICT signal.
   *
   * Unlike {@link getRowValidationStatus} this is NOT masked by the 'Edited'
   * display state (`forceEdited` / `isRowInEditMode`). The editor still DISPLAYS
   * a freshly-edited row as 'Edited' (pencil) with its error message; but the
   * published `hasErrors` must still reflect the error so an imbalanced row
   * cannot pass submission while a cell is mid-edit. This is what makes
   * evaluateSheetStatus() agree with the headless pure validator (which has no
   * edit-mode concept). Without it, editing a member so Total ≠ Σ members and
   * re-submitting in that window published hasErrors:false (the row read as
   * 'Edited', not 'Failed') while the pure validator correctly flagged it.
   */
  protected rowHasBlockingError(
    control: SchemaControl,
    consolidatedOrSeparate: 'Consolidated' | 'Separate'
  ): boolean {
    if (!control.ui.tableColumn) return false;

    const isRequired = this.isFieldRequired(control);
    const controlNames = MEMBER_SHORT_NAMES.map(member =>
      this.getXbrlControlName(control.qname, consolidatedOrSeparate, member)
    );

    let hasInvalid = false;
    let hasEmptyRequiredField = false;
    for (const controlName of controlNames) {
      const formControl = this.form.get(controlName);
      if (formControl) {
        if (formControl.invalid && (formControl.dirty || formControl.touched)) {
          hasInvalid = true;
        }
        if (isRequired && (formControl.value === null || formControl.value === undefined || formControl.value === '')) {
          hasEmptyRequiredField = true;
        }
      }
    }

    let hasColumnSumError = false;
    const rowErrors = this.validationErrors.get(control.qname);
    if (rowErrors && rowErrors.length > 0) {
      const consolidationLabel = consolidatedOrSeparate === 'Consolidated' ? 'Consolidated' : 'Separate';
      hasColumnSumError = rowErrors.some(error => error.includes(consolidationLabel));
    }

    return hasInvalid || hasEmptyRequiredField || hasColumnSumError;
  }

  /**
   * Get validation icon based on status
   */
  getValidationIcon(status: 'Failed' | 'Edited' | 'Validated*' | 'Validated'): string {
    switch (status) {
      case 'Validated':
      case 'Validated*':
        return 'pi pi-check-circle';
      case 'Failed':
        return 'pi pi-times-circle';
      case 'Edited':
        return 'pi pi-pencil';
      default:
        return '';
    }
  }

  /**
   * Get validation icon color
   */
  getValidationIconColor(status: 'Failed' | 'Edited' | 'Validated*' | 'Validated'): string {
    switch (status) {
      case 'Validated':
      case 'Validated*':
        return 'green';
      case 'Failed':
        return 'red';
      case 'Edited':
        return 'orange';
      default:
        return '';
    }
  }

  /**
   * Get all error messages for a row (from all 8 input columns including Total)
   * Groups errors by category: "Error" (red) and "* Suggested Change" (black)
   * Note: Role24 only has Error category (column sum errors and form validation errors)
   * Returns HTML string with <br> tags for rendering with innerHTML
   */
  getRowErrorDescription(control: SchemaControl, consolidatedOrSeparate: 'Consolidated' | 'Separate'): string {
    if (!control.ui.tableColumn) return '';
    const cache = consolidatedOrSeparate === 'Consolidated' ? this.rowErrorConsolidated : this.rowErrorSeparate;
    return cache.get(control.qname) ?? '';
  }

  /**
   * Actual error-description construction. Called by recomputeRowCaches() only —
   * the template binds to the precomputed Map via getRowErrorDescription().
   */
  private computeRowErrorDescription(control: SchemaControl, consolidatedOrSeparate: 'Consolidated' | 'Separate'): string {
    if (!control.ui.tableColumn) return '';

    const inputMembers = MEMBER_SHORT_NAMES;
    const memberLabels: { [key: string]: string } = {
      'Parent': 'Parent',
      'JointControl': 'Entities with joint control',
      'Subsidiaries': 'Subsidiaries',
      'Associates': 'Associates',
      'JointVentures': 'Joint ventures',
      'KeyManagement': 'Key management',
      'OtherRelatedParties': 'Other related parties',
      'Total': 'Total'
    };

    const errors: string[] = [];           // Red - Error category
    const suggestedChanges: string[] = []; // Black - * Suggested Change category (not used in Role24)

    // Check each column for required field with no value and form control validation errors -> Error category
    // Use isFieldRequired to check both required and requiredIf conditions
    const isRequired = this.isFieldRequired(control);

    for (const member of inputMembers) {
      const controlName = this.getXbrlControlName(control.qname, consolidatedOrSeparate, member);
      const formControl = this.form.get(controlName);

      if (formControl) {
        const errorMessages: string[] = [];

        // Check required field with no value (field by field)
        if (isRequired && (formControl.value === null || formControl.value === undefined || formControl.value === '')) {
          errorMessages.push('This is a mandatory field');
        }

        // Check form control validation errors
        if (formControl.invalid && (formControl.dirty || formControl.touched)) {
          if (formControl.errors) {
            if (formControl.errors['required']) {
              // Only add if not already added as mandatory field
              if (!errorMessages.includes('This is a mandatory field')) {
                errorMessages.push('This is a mandatory field');
              }
            }
            if (formControl.errors['min']) {
              errorMessages.push(`Minimum value: ${formControl.errors['min'].min}`);
            }
            if (formControl.errors['max']) {
              errorMessages.push(`Maximum value: ${formControl.errors['max'].max}`);
            }
          }
        }

        if (errorMessages.length > 0) {
          errors.push(`${memberLabels[member]}: ${errorMessages.join(', ')}`);
        }
      }
    }

    // Add column sum validation errors for this consolidation type -> Error category
    const consolidationLabel = consolidatedOrSeparate === 'Consolidated' ? 'Consolidated' : 'Separate';
    const rowErrors = this.validationErrors.get(control.qname);
    if (rowErrors && rowErrors.length > 0) {
      // Filter errors for this consolidation type and reformat the message
      const relevantErrors = rowErrors
        .filter(error => error.includes(consolidationLabel))
        .map(error => {
          // Remove [Consolidated ] and [Separate ] labels
          let cleaned = error
            .replace(/\s*\[Consolidated\s*\]/g, '')
            .replace(/\s*\[Separate\s*\]/g, '');

          // Reformat column sum validation error message
          // Original: "Total related party: Total related party should equal to the sum of:\n- Parent\n..."
          // Desired: "Total related party should equal to the sum of\n- Parent\n..."
          const sumErrorMatch = cleaned.match(/^([^:]+):\s*\1\s*should equal to the sum of:\s*([\s\S]*)$/);
          if (sumErrorMatch) {
            const label = sumErrorMatch[1].trim();
            const components = sumErrorMatch[2].trim();
            // Format exactly as user specified with line breaks
            cleaned = `${label} should equal to the sum of<br>${components.replace(/\n/g, '<br>')}`;
          } else {
            // Convert any remaining newlines to <br> for HTML display
            cleaned = cleaned.replace(/\n/g, '<br>');
          }

          return cleaned;
        });
      errors.push(...relevantErrors);
    }

    // Format output grouped by category with CSS classes
    // Error section in red (.error-text), Suggested Change section in black (.suggested-change-text)
    let result = '';

    if (errors.length > 0) {
      result += '<span class="error-text">ERROR</span><br>';
      result += '<span class="error-text">' + errors.map((errorText, errorIndex) => `${errorIndex + 1}. ${errorText}`).join('<br><br>') + '</span>';
    }

    if (suggestedChanges.length > 0) {
      if (result) result += '<br><br>';
      result += '<span class="error-text">*</span><span class="suggested-change-text"> SUGGESTED CHANGE</span><br>';
      result += '<span class="suggested-change-text">' + suggestedChanges.map((changeText, changeIndex) => `${changeIndex + 1}. ${changeText}`).join('<br><br>') + '</span>';
    }

    return result;
  }

  /**
   * Check if row has any "Error" category errors (red text)
   * Error category includes: form validation errors, column sum errors, empty required fields
   * Note: Also checks for empty required fields even when controls are disabled
   */
  hasErrorCategory(control: SchemaControl, consolidatedOrSeparate: 'Consolidated' | 'Separate'): boolean {
    if (!control.ui.tableColumn) return false;
    const cache = consolidatedOrSeparate === 'Consolidated'
      ? this.rowHasErrorCategoryConsolidated
      : this.rowHasErrorCategorySeparate;
    return cache.get(control.qname) ?? false;
  }

  /**
   * Actual computation for hasErrorCategory. Called by recomputeRowCaches() only.
   */
  private computeRowHasErrorCategory(control: SchemaControl, consolidatedOrSeparate: 'Consolidated' | 'Separate'): boolean {
    if (!control.ui.tableColumn) return false;

    const inputMembers = MEMBER_SHORT_NAMES;
    const isRequired = this.isFieldRequired(control);

    // Check form control validation errors and empty required fields
    for (const member of inputMembers) {
      const controlName = this.getXbrlControlName(control.qname, consolidatedOrSeparate, member);
      const formControl = this.form.get(controlName);

      if (formControl) {
        // Check for empty required fields (works even for disabled controls)
        // This is needed because disabled controls always return invalid=false in Angular
        if (isRequired && (formControl.value === null || formControl.value === undefined || formControl.value === '')) {
          return true;
        }

        // Check if invalid (for enabled controls)
        if (formControl.invalid && (formControl.dirty || formControl.touched)) {
          return true;
        }
      }
    }

    // Check column sum validation errors
    const consolidationLabel = consolidatedOrSeparate === 'Consolidated' ? 'Consolidated' : 'Separate';
    const rowErrors = this.validationErrors.get(control.qname);
    if (rowErrors && rowErrors.length > 0) {
      const hasErrorForThisConsolidation = rowErrors.some(error => error.includes(consolidationLabel));
      if (hasErrorForThisConsolidation) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if row has any "Suggested Change" category errors (black text)
   * Suggested Change category includes: API value mismatch warnings
   * Note: Role24 does not have API mismatch validation, always returns false
   */
  hasSuggestedChangeCategory(control: SchemaControl, consolidatedOrSeparate: 'Consolidated' | 'Separate'): boolean {
    // Role24 does not have API mismatch warnings
    return false;
  }

  /**
   * Check if a row has failed validation (for row background styling)
   * Note: Keeps red background even during edit mode if row has errors
   */
  hasRowFailed(control: SchemaControl, consolidatedOrSeparate: 'Consolidated' | 'Separate'): boolean {
    return this.getRowValidationStatus(control, consolidatedOrSeparate) === 'Failed';
  }

  /**
   * Evaluate sheet status by looping through all rows' mapping status.
   * Checks both Consolidated and Separate tables.
   * Sets hasErrors = true if any row has 'Failed' status, false otherwise.
   * Called at init and before submit.
   */
  evaluateSheetStatus(): void {
    if (!this.schema?.controls) return;

    let hasErrors = false;

    // Check non-table fields (e.g. required TextBlock) via form control validity
    for (const control of this.schema.controls) {
      if (!control.ui.tableColumn && this.isFieldRequired(control)) {
        const formControl = this.form.get(control.qname);
        if (formControl && formControl.invalid) {
          hasErrors = true;
          break;
        }
      }
    }

    // Check table rows via mapping status
    if (!hasErrors) {
      // Only the rendered dimensions count toward the verdict — a Separate
      // filing hides the Consolidated table, so its cells must not block.
      const types = this.activeDimensions;

      for (const type of types) {
        if (hasErrors) break;
        for (const control of this.schema.controls) {
          // Verdict uses the UNMASKED error check, so an in-edit-mode row that
          // breaks the column-sum still blocks (displays 'Edited', verdict true).
          if (this.rowHasBlockingError(control, type)) {
            hasErrors = true;
            break;
          }
        }
      }
    }

    this.stateService.updateSheetLevelStatus(SHEET_KEYS.notesRelatedPartyTransactions, { hasErrors });
  }

  /**
   * Build the per-dimension edit-mode key for a row. The Consolidated and Separate
   * tables render the same `control` (same qname); namespacing the key by dimension
   * keeps their edit state independent so the pencil in one table no longer flips the
   * same-named row in the other. Mirrors role19's getEditKey.
   */
  private getEditKey(control: SchemaControl, dimension: 'Consolidated' | 'Separate'): string {
    return `${control.qname}|${dimension}`;
  }

  /**
   * The 8 editable input controls (all related-party members incl. Total) for a single
   * dimension of a row — the unit that one edit session enables/reverts. Only this
   * dimension's cells participate, never the other table's.
   */
  private editableControlPaths(control: SchemaControl, dimension: 'Consolidated' | 'Separate'): string[] {
    return MEMBER_SHORT_NAMES.map(member =>
      this.getXbrlControlName(control.qname, dimension, member)
    );
  }

  /**
   * Check if a row is currently in edit mode for the given dimension.
   */
  isRowInEditMode(control: SchemaControl, dimension: 'Consolidated' | 'Separate'): boolean {
    return this.editingRows.has(this.getEditKey(control, dimension));
  }

  /**
   * Enter edit mode for one dimension of a row (enable its 8 input controls incl. Total).
   * Scope is the single dimension the user clicked into, not both tables.
   */
  enterEditMode(control: SchemaControl, dimension: 'Consolidated' | 'Separate'): void {
    // Don't allow editing before validation is complete
    if (!this.isValidationComplete) {
      return;
    }
    // Don't allow edit mode for readonly or calculated fields
    if (control.ui.readonly || control.ui.calculated) {
      return;
    }

    // Capture the committed badge BEFORE entering edit mode (isRowInEditMode is still false here).
    this.frozenRowStatus.set(this.getEditKey(control, dimension), this.getRowValidationStatus(control, dimension));

    // Store original values for this dimension's 8 input controls (including Total)
    const originalValues: { [key: string]: number | null } = {};
    const controlNames = this.editableControlPaths(control, dimension);

    // Store values and enable this dimension's controls
    controlNames.forEach(name => {
      const formControl = this.form.get(name);
      if (formControl) {
        originalValues[name] = formControl.value ?? null;
        enableAndRevealErrors(formControl, { emitEvent: true });
      }
    });

    this.editingRows.set(this.getEditKey(control, dimension), originalValues);
    this.stateService.markSheetEditing(SHEET_KEYS.notesRelatedPartyTransactions);
    this.recomputeRowCaches();
  }

  /**
   * Save edit mode changes (validate and persist)
   * Checks if values differ from original API data:
   * - If different: marks as 'Edited'
   * - If equal to initial API values: restores to initial status ('Validated' or '')
   * Note: Allows saving even with validation errors (empty required fields)
   */
  saveEditMode(control: SchemaControl, dimension: 'Consolidated' | 'Separate'): void {
    const editKey = this.getEditKey(control, dimension);
    if (!this.editingRows.has(editKey)) {
      return;
    }

    const controlNames = this.editableControlPaths(control, dimension);

    // Disable this dimension's controls and exit edit mode FIRST so caches and the
    // verdict recompute as committed (not in-edit).
    controlNames.forEach((name) => {
      const formControl = this.form.get(name);
      if (formControl) {
        formControl.disable({ emitEvent: false });
      }
    });
    this.frozenRowStatus.delete(editKey);
    this.editingRows.delete(editKey);
    this.recomputeRowCaches();

    // Commit: persist ONLY this row+dimension's cells (subset merge).
    const subset = pickSubset(this.form.getRawValue(), controlNames);
    this.stateService.updateSheetData(SHEET_KEYS.notesRelatedPartyTransactions, subset);

    // Same-sheet revalidate + cross-sheet (requiredIf) now runs, then reconcile
    // pencil + leave editing mode (restores pure substitution) + publish verdict.
    this.validateColumnSum();
    this.updateRequiredIfValidators();
    this.stateService.reconcileEditedFlag(SHEET_KEYS.notesRelatedPartyTransactions);
    this.stateService.clearSheetEditing(SHEET_KEYS.notesRelatedPartyTransactions);
    this.evaluateSheetStatus();
  }

  /**
   * Cancel edit mode - always revert to the snapshot captured when entering edit mode.
   * Scoped to the dimension the user is editing.
   */
  cancelEditMode(control: SchemaControl, dimension: 'Consolidated' | 'Separate'): void {
    const editKey = this.getEditKey(control, dimension);
    if (!this.editingRows.has(editKey)) {
      return;
    }

    const controlNames = this.editableControlPaths(control, dimension);

    // Revert to the LATEST ADOPTED state rather than the entry-time snapshot, so a
    // sibling tab's confirmed edit that arrived while this row was open is honoured
    // on cancel (P.O. rule 2026-08-17). Falls back to the entry snapshot for any
    // control the state does not carry.
    //
    // In the single-tab case the two are identical — the row is disabled and
    // unwritten for the whole time it is open — so this is a provable no-op there.
    const originalValues = this.editingRows.get(editKey);
    const adopted = this.stateService.getSheetData(SHEET_KEYS.notesRelatedPartyTransactions);
    if (originalValues) {
      controlNames.forEach((controlName) => {
        const formControl = this.form.get(controlName);
        if (formControl) {
          const revertValue = controlName in adopted
            ? adopted[controlName]
            : (originalValues[controlName] ?? null);
          formControl.setValue(revertValue, { emitEvent: false });
          formControl.markAsPristine();
          formControl.disable({ emitEvent: false });
        }
      });
    }

    // Exit edit mode BEFORE recomputing so the row is no longer 'forceEdited'
    // (fixes the "stuck on Edited" bug).
    this.frozenRowStatus.delete(editKey);
    this.editingRows.delete(editKey);
    this.validateColumnSum(); // re-validates + recomputeRowCaches() over the reverted, no-longer-editing row
    this.stateService.clearSheetEditing(SHEET_KEYS.notesRelatedPartyTransactions);
    this.evaluateSheetStatus();
  }

  onCancelConfirmed(): void {
    // Clear all editing states
    this.editingRows.clear();
    this.frozenRowStatus.clear();
    this.stateService.clearSheetEditing(SHEET_KEYS.notesRelatedPartyTransactions);

    // Reset validation complete flag before reloading
    this.isValidationComplete = false;

    // Reload from the reverted state service data
    this.loadSavedState();
    this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
    this.form.markAsPristine();
  }

  /**
   * Memoised per-control numeric policy — this sheet's single declaration
   * point for decimals, grouping and the integer-digit cap, all resolved from
   * the control's own schema record rather than hand-written per binding.
   *
   * Memoised because templates call this once per binding inside an `*ngFor`;
   * an unmemoised `resolveNumericPolicy` would re-resolve on every
   * change-detection cycle. Keyed by `qname`, unique per control in a sheet.
   *
   * `[min]`/`[max]` are deliberately NOT bound from here — `SchemaNumericDirective`
   * assigns them onto the PrimeNG instance directly. See that directive's class
   * docblock for why a template binding coerces an unconstrained `undefined`
   * to `NaN` and silently blocks the minus key on signed monetary fields.
   */
  private policyCache = new Map<string, NumericFieldPolicy>();

  numericPolicy(control: SchemaControl): NumericFieldPolicy {
    const cached = this.policyCache.get(control.qname);
    if (cached)
      return cached;
    const resolved = resolveNumericPolicy(control);
    this.policyCache.set(control.qname, resolved);
    return resolved;
  }

}
